import React, { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Slider,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Refresh,
  PictureAsPdf,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  CloudUpload,
  Delete,
} from '@mui/icons-material';
import { useDocumentCustomization } from '../hooks/useDocumentCustomization';
import { DocumentPreview } from '../components/customization/DocumentPreview';
import { FontFamily, LogoPosition, TableStyle } from '../types/documentCustomization.types';
import { useNotification } from '../contexts/NotificationContext';

export const DocumentCustomizationPage: React.FC = () => {
  const [previewType, setPreviewType] = useState<'facture' | 'devis'>('facture');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const { showSuccess, showError, showConfirm } = useNotification();

  const {
    customization,
    loading,
    saving,
    error,
    hasChanges,
    updateCustomization,
    uploadLogo,
    deleteLogo,
    resetToDefault,
    generateTestPDF,
  } = useDocumentCustomization();

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation
    const acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'];
    if (!acceptedFormats.includes(file.type)) {
      showError('Format non accepté. Utilisez PNG, JPG ou SVG.');
      return;
    }

    const maxSize = 2 * 1024 * 1024; // 2MB
    if (file.size > maxSize) {
      showError('Fichier trop volumineux. Taille maximale : 2 MB.');
      return;
    }

    try {
      await uploadLogo(file);
      showSuccess('Logo uploadé avec succès');
    } catch (err) {
      console.error('Erreur upload:', err);
    }
  };

  const handleLogoDelete = async () => {
    const confirmed = await showConfirm({
      title: 'Supprimer le logo',
      message: 'Voulez-vous vraiment supprimer le logo de vos documents ?',
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      severity: 'error',
    });

    if (confirmed) {
      try {
        await deleteLogo();
        showSuccess('Logo supprimé avec succès');
      } catch (err) {
        console.error('Erreur suppression:', err);
      }
    }
  };

  const handleReset = async () => {
    const confirmed = await showConfirm({
      title: 'Réinitialiser la personnalisation',
      message: 'Voulez-vous vraiment réinitialiser toutes les personnalisations aux valeurs par défaut ? Cette action est irréversible.',
      confirmText: 'Réinitialiser',
      cancelText: 'Annuler',
      severity: 'warning',
    });

    if (confirmed) {
      try {
        await resetToDefault();
        showSuccess('Personnalisation réinitialisée avec succès');
      } catch (err) {
        console.error('Erreur réinitialisation:', err);
      }
    }
  };

  const handleGenerateTestPDF = async () => {
    try {
      await generateTestPDF(previewType);
      showSuccess('PDF de test généré avec succès');
    } catch (err) {
      console.error('Erreur génération PDF:', err);
    }
  };

  // Couleurs prédéfinies
  const predefinedColors = [
    { name: 'Gris clair', value: '#95a5a6' },
    { name: 'Gris foncé', value: '#2c3e50' },
    { name: 'Bleu clair', value: '#a2c4e0' },
    { name: 'Bleu', value: '#3498db' },
    { name: 'Bleu marine', value: '#2980b9' },
    { name: 'Bleu nuit', value: '#34495e' },
    { name: 'Bleu canard', value: '#16a085' },
    { name: 'Vert clair', value: '#52c234' },
    { name: 'Vert citron', value: '#a4c639' },
    { name: 'Vert foncé', value: '#27ae60' },
    { name: 'Jaune', value: '#f1c40f' },
    { name: 'Orange', value: '#e67e22' },
    { name: 'Rouge', value: '#e74c3c' },
    { name: 'Bordeaux', value: '#c0392b' },
    { name: 'Violet foncé', value: '#6c1a45' },
    { name: 'Violet', value: '#8e44ad' },
    { name: 'Magenta', value: '#c91f7f' },
    { name: 'Rose', value: '#ec87bf' },
    { name: 'Marron', value: '#a0522d' },
    { name: 'Noir', value: '#000000' },
  ];

  const handleColorSelect = (colorType: 'primary' | 'secondary' | 'accent', color: string) => {
    if (colorType === 'primary') {
      updateCustomization({ primaryColor: color });
    } else if (colorType === 'secondary') {
      updateCustomization({ secondaryColor: color });
    } else {
      updateCustomization({ accentColor: color });
    }
    setShowColorPicker(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Personnalisation des Documents
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
          Personnalisez l'apparence de vos factures et devis
        </Typography>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Section des outils de personnalisation */}
      <Paper elevation={2} sx={{ p: 2.5, mb: 3 }}>
        {/* Section Visuels */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, fontSize: '1.1rem' }}>
            Visuels
          </Typography>

          {/* Logo */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Logo
            </Typography>

            {/* Aperçu du logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              {customization.logoUrl ? (
                <Box sx={{ position: 'relative' }}>
                  <Box
                    component="img"
                    src={`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/${customization.logoUrl}`}
                    alt="Logo actuel"
                    sx={{
                      maxWidth: '120px',
                      maxHeight: '60px',
                      objectFit: 'contain',
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      p: 0.5,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <IconButton
                    onClick={handleLogoDelete}
                    disabled={saving}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      bgcolor: 'error.main',
                      color: 'white',
                      width: 24,
                      height: 24,
                      '&:hover': { bgcolor: 'error.dark' },
                    }}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  sx={{
                    width: 70,
                    height: 70,
                    borderRadius: '50%',
                    bgcolor: '#e0e0e0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#999',
                    fontSize: '9px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                  }}
                >
                  VOTRE
                  <br />
                  LOGO
                </Box>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUpload />}
                disabled={saving}
                size="small"
                sx={{ fontSize: '0.8rem' }}
              >
                Ajouter votre logo
                <input
                  type="file"
                  hidden
                  accept="image/png,image/jpeg,image/jpg,image/svg+xml"
                  onChange={handleLogoUpload}
                />
              </Button>
            </Box>

            <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1.5, fontSize: '0.7rem' }}>
              Formats acceptés : PNG, JPG, SVG (max 2 MB)
            </Typography>

            {/* Alignement et Taille */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" gutterBottom sx={{ fontWeight: 600, display: 'block', fontSize: '0.8rem' }}>
                  Alignement
                </Typography>
                <ToggleButtonGroup
                  value={customization.logoPosition || 'LEFT'}
                  exclusive
                  onChange={(_, value) => {
                    if (value) updateCustomization({ logoPosition: value as LogoPosition });
                  }}
                  disabled={saving}
                  size="small"
                  sx={{ display: 'flex', gap: 0.5 }}
                >
                  <ToggleButton value="LEFT" sx={{ flex: 1, py: 0.5 }}>
                    <FormatAlignLeft fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="CENTER" sx={{ flex: 1, py: 0.5 }}>
                    <FormatAlignCenter fontSize="small" />
                  </ToggleButton>
                  <ToggleButton value="RIGHT" sx={{ flex: 1, py: 0.5 }}>
                    <FormatAlignRight fontSize="small" />
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="caption" gutterBottom sx={{ fontWeight: 600, display: 'block', fontSize: '0.8rem' }}>
                  Taille: {customization.logoWidth || 120}px
                </Typography>
                <Slider
                  value={customization.logoWidth || 120}
                  onChange={(_, value) => updateCustomization({ logoWidth: value as number })}
                  min={80}
                  max={300}
                  step={10}
                  marks
                  valueLabelDisplay="auto"
                  disabled={saving}
                  size="small"
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Section Couleurs */}
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, fontSize: '1.1rem' }}>
            Couleurs
          </Typography>

          {/* Couleur Principale */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Couleur principale
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {predefinedColors.map((color) => (
                <Box
                  key={color.value}
                  onClick={() => handleColorSelect('primary', color.value)}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: color.value,
                    cursor: 'pointer',
                    border:
                      customization.primaryColor === color.value
                        ? '2.5px solid #000'
                        : '1.5px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                  title={color.name}
                />
              ))}
            </Box>
            <TextField
              size="small"
              value={customization.primaryColor || '#2c3e50'}
              onChange={(e) => updateCustomization({ primaryColor: e.target.value })}
              placeholder="#2c3e50"
              disabled={saving}
              sx={{ width: 130 }}
              inputProps={{ style: { textTransform: 'uppercase', fontSize: '0.8rem' } }}
            />
          </Box>

          {/* Couleur Secondaire */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Couleur secondaire
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {predefinedColors.map((color) => (
                <Box
                  key={color.value}
                  onClick={() => handleColorSelect('secondary', color.value)}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: color.value,
                    cursor: 'pointer',
                    border:
                      customization.secondaryColor === color.value
                        ? '2.5px solid #000'
                        : '1.5px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                  title={color.name}
                />
              ))}
            </Box>
            <TextField
              size="small"
              value={customization.secondaryColor || '#3498db'}
              onChange={(e) => updateCustomization({ secondaryColor: e.target.value })}
              placeholder="#3498db"
              disabled={saving}
              sx={{ width: 130 }}
              inputProps={{ style: { textTransform: 'uppercase', fontSize: '0.8rem' } }}
            />
          </Box>

          {/* Couleur Accent */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
              Couleur accent (totaux)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
              {predefinedColors.map((color) => (
                <Box
                  key={color.value}
                  onClick={() => handleColorSelect('accent', color.value)}
                  sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: color.value,
                    cursor: 'pointer',
                    border:
                      customization.accentColor === color.value
                        ? '2.5px solid #000'
                        : '1.5px solid #e0e0e0',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'scale(1.1)',
                    },
                  }}
                  title={color.name}
                />
              ))}
            </Box>
            <TextField
              size="small"
              value={customization.accentColor || '#27ae60'}
              onChange={(e) => updateCustomization({ accentColor: e.target.value })}
              placeholder="#27ae60"
              disabled={saving}
              sx={{ width: 130 }}
              inputProps={{ style: { textTransform: 'uppercase', fontSize: '0.8rem' } }}
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2.5 }} />

        {/* Options avancées */}
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 1.5, fontSize: '1.1rem' }}>
            Options avancées
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.85rem' }}>Police de caractères</InputLabel>
                <Select
                  value={customization.fontFamily || 'Helvetica'}
                  onChange={(e) => updateCustomization({ fontFamily: e.target.value as FontFamily })}
                  disabled={saving}
                  label="Police de caractères"
                  sx={{ fontSize: '0.85rem' }}
                >
                  <MenuItem value="Helvetica" sx={{ fontSize: '0.85rem' }}>Helvetica</MenuItem>
                  <MenuItem value="Arial" sx={{ fontSize: '0.85rem' }}>Arial</MenuItem>
                  <MenuItem value="Times-Roman" sx={{ fontSize: '0.85rem' }}>Times New Roman</MenuItem>
                  <MenuItem value="Courier" sx={{ fontSize: '0.85rem' }}>Courier</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ fontSize: '0.85rem' }}>Style de tableau</InputLabel>
                <Select
                  value={customization.tableStyle || 'CLASSIC'}
                  onChange={(e) => updateCustomization({ tableStyle: e.target.value as TableStyle })}
                  disabled={saving}
                  label="Style de tableau"
                  sx={{ fontSize: '0.85rem' }}
                >
                  <MenuItem value="CLASSIC" sx={{ fontSize: '0.85rem' }}>Classique (lignes alternées)</MenuItem>
                  <MenuItem value="MINIMAL" sx={{ fontSize: '0.85rem' }}>Minimal</MenuItem>
                  <MenuItem value="BOLD" sx={{ fontSize: '0.85rem' }}>Gras</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                size="small"
                label="Message personnalisé (pied de page)"
                value={customization.footerText || ''}
                onChange={(e) => updateCustomization({ footerText: e.target.value })}
                placeholder="Ex: Merci de votre confiance..."
                disabled={saving}
                InputLabelProps={{ sx: { fontSize: '0.85rem' } }}
                inputProps={{ style: { fontSize: '0.85rem' } }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customization.showLegalFooter ?? true}
                    onChange={(e) => updateCustomization({ showLegalFooter: e.target.checked })}
                    disabled={saving}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: '0.85rem' }}>Afficher les mentions légales</Typography>}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customization.showPageNumber ?? true}
                    onChange={(e) => updateCustomization({ showPageNumber: e.target.checked })}
                    disabled={saving}
                    size="small"
                  />
                }
                label={<Typography sx={{ fontSize: '0.85rem' }}>Afficher les numéros de page</Typography>}
              />
            </Grid>

            {/* Options spécifiques aux factures */}
            {previewType === 'facture' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1f2937', mb: 1.5, fontSize: '0.9rem' }}>
                    Sections de la facture
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={customization.showBankDetails ?? true}
                        onChange={(e) => updateCustomization({ showBankDetails: e.target.checked })}
                        disabled={saving}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.85rem' }}>Afficher les coordonnées bancaires</Typography>}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={customization.showInsuranceSection ?? true}
                        onChange={(e) => updateCustomization({ showInsuranceSection: e.target.checked })}
                        disabled={saving}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.85rem' }}>Afficher la section assurance</Typography>}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={customization.showQualifications ?? true}
                        onChange={(e) => updateCustomization({ showQualifications: e.target.checked })}
                        disabled={saving}
                        size="small"
                      />
                    }
                    label={<Typography sx={{ fontSize: '0.85rem' }}>Afficher les qualifications</Typography>}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </Box>

        {/* Boutons d'action */}
        <Box sx={{ mt: 3, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh fontSize="small" />}
            onClick={handleReset}
            disabled={saving}
            color="warning"
            size="small"
            sx={{ fontSize: '0.8rem' }}
          >
            Réinitialiser
          </Button>
          <Button
            variant="contained"
            startIcon={<PictureAsPdf fontSize="small" />}
            onClick={handleGenerateTestPDF}
            disabled={saving}
            size="small"
            sx={{ bgcolor: '#2196F3', '&:hover': { bgcolor: '#1976D2' }, fontSize: '0.8rem' }}
          >
            Générer PDF de test
          </Button>
        </Box>

        {hasChanges && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Les modifications sont sauvegardées automatiquement
          </Alert>
        )}
      </Paper>

      {/* Section Prévisualisation - EN BAS */}
      <Paper elevation={2} sx={{ p: 2 }}>
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
            Aperçu en temps réel
          </Typography>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel sx={{ fontSize: '0.85rem' }}>Type</InputLabel>
            <Select
              value={previewType}
              onChange={(e) => setPreviewType(e.target.value as 'facture' | 'devis')}
              label="Type"
              sx={{ fontSize: '0.85rem' }}
            >
              <MenuItem value="facture" sx={{ fontSize: '0.85rem' }}>Facture</MenuItem>
              <MenuItem value="devis" sx={{ fontSize: '0.85rem' }}>Devis</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <DocumentPreview customization={customization} type={previewType} />
      </Paper>
    </Container>
  );
};

export default DocumentCustomizationPage;
